import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Modal } from '../modal'
import { Button } from '../button'
import { CustomSelect } from '../custom-select'
import { SelectWrapper } from '../select-wrapper'
import { exportTemplateQueries } from 'src/services/export-templates/queries'
import { SelectOption } from 'src/services/base-model'

interface GenerateReportModalProps {
  isOpen: boolean
  onClose: () => void
  onGenerate: (templateId: string | null) => void
  organizationId?: string
  defaultTemplateRef?: { id: string; name: string } | null
  isGenerating?: boolean
}

export const GenerateReportModal: React.FC<GenerateReportModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  organizationId,
  defaultTemplateRef,
  isGenerating,
}) => {
  const { t } = useTranslation()
  const [selectedTemplate, setSelectedTemplate] = useState<SelectOption[]>(() => {
    if (defaultTemplateRef) {
      return [{ label: defaultTemplateRef.name, value: defaultTemplateRef.id }]
    }
    return []
  })

  // Reset selection when modal opens with new default
  React.useEffect(() => {
    if (isOpen) {
      if (defaultTemplateRef) {
        setSelectedTemplate([{ label: defaultTemplateRef.name, value: defaultTemplateRef.id }])
      } else {
        setSelectedTemplate([])
      }
    }
  }, [isOpen, defaultTemplateRef])

  const { data: templates, isLoading: loadingTemplates } = useQuery(
    exportTemplateQueries.list({
      filters: organizationId ? { organization: organizationId } : undefined,
    }),
  )

  const templateOptions = useMemo(
    () =>
      templates?.results?.map((t) => ({
        label: t.name,
        value: t.id,
      })) || [],
    [templates],
  )

  const handleGenerate = () => {
    const templateId = selectedTemplate.length > 0 ? selectedTemplate[0].value : null
    onGenerate(templateId)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('generateReport.title')} size="sm">
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-surface p-4">
          <SelectWrapper
            label={t('generateReport.templateLabel')}
            data-testid="generate-template-select-wrapper"
          >
            <CustomSelect
              searchable
              clearable
              options={templateOptions}
              values={selectedTemplate}
              loading={loadingTemplates}
              placeholder={t('generateReport.systemDefaultPlaceholder')}
              onChange={(values) => setSelectedTemplate(values)}
              data-testid="generate-template-select"
            />
          </SelectWrapper>
          <p className="mt-2 text-xs text-text-placeholder">
            {selectedTemplate.length > 0
              ? t('generateReport.templateHint')
              : t('generateReport.noTemplateHint')}
          </p>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isGenerating}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleGenerate}
            disabled={isGenerating}
            data-testid="confirm-generate-button"
          >
            {isGenerating ? t('common.generating') : t('generateReport.generateBtn')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
